import { staffToken } from './msal';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

type Who = 'partner' | 'staff' | 'public';

/**
 * Every request ends. A fetch with no deadline can stay pending for ever (a dropped connection, a
 * proxy that never answers), and a button bound to it stays disabled with nothing to say why.
 */
export const API_TIMEOUT_MS = 30_000;
/** Uploads carry the whole file over the partner's connection, so they get longer. */
export const UPLOAD_TIMEOUT_MS = 180_000;

export const MESSAGES = {
  offline: 'We could not reach the Partner Hub. Check your connection and try again.',
  timeout: 'The Partner Hub took too long to answer, so we stopped waiting. Check your connection and try again.',
  uploadFailed: 'Your file could not be uploaded. Check your connection and try again; if it keeps happening, contact your partner manager.',
  uploadTimeout: 'The upload took too long, so we stopped it. Try a smaller file or a faster connection.',
} as const;

/**
 * fetch with a deadline. A timeout becomes an ApiError with `onTimeout`; any other network failure
 * (the browser's bare "Failed to fetch", "Load failed" or "NetworkError…") becomes one with `onNetwork`.
 * An abort the caller asked for is passed through untouched.
 */
async function fetchWithDeadline(url: string, init: RequestInit, ms: number, onTimeout: string, onNetwork: string): Promise<Response> {
  const ctl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctl.abort();
  }, ms);
  const outer = init.signal;
  const relay = () => ctl.abort();
  if (outer) {
    if (outer.aborted) ctl.abort();
    else outer.addEventListener('abort', relay, { once: true });
  }
  try {
    return await fetch(url, { ...init, signal: ctl.signal });
  } catch (err) {
    if (timedOut) throw new ApiError(0, onTimeout);
    if (outer?.aborted) throw err;
    throw new ApiError(0, onNetwork);
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener('abort', relay);
  }
}

/** JSON fetch against the Hub API. Partner calls use the session cookie; staff calls a bearer token. */
export async function api<T>(path: string, init: RequestInit & { who?: Who; json?: unknown; timeoutMs?: number } = {}): Promise<T> {
  const { who = 'partner', json, timeoutMs = API_TIMEOUT_MS, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (json !== undefined) headers.set('Content-Type', 'application/json');
  if (who === 'staff') headers.set('Authorization', `Bearer ${await staffToken()}`);
  const r = await fetchWithDeadline(
    `/api${path}`,
    { credentials: 'same-origin', ...rest, headers, body: json !== undefined ? JSON.stringify(json) : rest.body },
    timeoutMs,
    MESSAGES.timeout,
    MESSAGES.offline,
  );
  if (!r.ok) {
    let msg = `Request failed (${r.status})`;
    try {
      const b = (await r.json()) as { message?: string | string[]; error?: string };
      msg = (Array.isArray(b.message) ? b.message[0] : b.message) ?? b.error ?? msg;
    } catch {
      /* not JSON */
    }
    throw new ApiError(r.status, friendly(r.status, msg));
  }
  if (r.status === 204) return undefined as T;
  const type = r.headers.get('content-type') ?? '';
  return (type.includes('json') ? r.json() : r.text()) as Promise<T>;
}

function friendly(status: number, msg: string): string {
  if (msg === 'not_approved_yet') return 'This opens once your application is approved.';
  if (msg === 'step_up_required') return 'For your security, confirm it is you with a code first.';
  if (msg === 'partnership_not_active') return 'Your partnership is not active.';
  if (msg === 'not_a_partner_hub_user') return 'Your Microsoft account has not been added to the Partner Hub. Ask a Partner Hub admin.';
  if (status === 401) return 'Please sign in again.';
  if (status === 429) return msg.includes('Try') ? msg : 'Too many attempts. Please wait a minute.';
  return msg.replace(/^body: /, '');
}

/**
 * Uploads a file straight to private storage with a short-lived link from the API. The storage
 * account must allow this site's origin (CORS); without it every browser upload fails before a
 * byte is sent, which is why a failure here gets its own plain message rather than the browser's.
 */
export async function putBlob(uploadUrl: string, file: File, timeoutMs = UPLOAD_TIMEOUT_MS): Promise<void> {
  const r = await fetchWithDeadline(
    uploadUrl,
    { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' }, body: file },
    timeoutMs,
    MESSAGES.uploadTimeout,
    MESSAGES.uploadFailed,
  );
  if (!r.ok) throw new ApiError(r.status, MESSAGES.uploadFailed);
}
